/*
 * =================================================================
 * Notifications Container Component (notifications-container.component.ts)
 * This component subscribes to the service and displays the list of notifications.
 * Place its selector, e.g., `<app-notifications-container></app-notifications-container>`,
 * in your main `app.component.html`.
 * =================================================================
 */
import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';

import { map, Observable } from 'rxjs';

import { GlobalNotificationService } from '@common/service/global-notification.service';
import { GlobalNotification, NotificationPosition } from '@common/types';

import { GlobalNotificationComponent } from './global-notification/global-notification.component';

// Import necessary services and models
// import { NotificationService } from './notification.service';
// import { Notification } from './notification.model';

@Component({
  selector: 'app-notifications-container',
  imports: [CommonModule, GlobalNotificationComponent],
  template: `
    @for (position of positions; track position) {
      <div class="notifications-container p-4" [ngClass]="position">
        @for (notification of notificationsByPosition[position] | async; track notification.id) {
          <app-global-notification [notification]="notification" (closed)="onClose(notification.id)">
          </app-global-notification>
        }
      </div>
    }
  `,
  styles: [
    `
      .notifications-container {
        position: fixed;
        z-index: 9999;
        display: flex;
        flex-direction: column;
        gap: 1rem;
        padding: 1rem;
        width: 24rem; /* 384px */
        max-width: calc(100% - 2rem);
        pointer-events: none;
      }

      /* Corner positions */
      .top-left {
        top: 0;
        left: 0;
        align-items: flex-start;
      }
      .top-right {
        top: 0;
        right: 0;
        align-items: flex-end;
      }
      .bottom-left {
        bottom: 0;
        left: 0;
        justify-content: flex-end;
        align-items: flex-start;
      }
      .bottom-right {
        bottom: 0;
        right: 0;
        justify-content: flex-end;
        align-items: flex-end;
      }

      /* Center-edge positions */
      .top-center {
        top: 0;
        left: 50%;
        transform: translateX(-50%);
        align-items: center;
      }
      .bottom-center {
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        justify-content: flex-end;
        align-items: center;
      }
      .middle-left {
        top: 50%;
        left: 0;
        transform: translateY(-50%);
        align-items: flex-start;
      }
      .middle-right {
        top: 50%;
        right: 0;
        transform: translateY(-50%);
        align-items: flex-end;
      }

      /* True center position */
      .center-center {
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        align-items: center;
      }
    `,
  ],
})
export class NotificationsContainerComponent {
  public positions: NotificationPosition[] = [
    'top-left',
    'top-center',
    'top-right',
    'middle-left',
    'center-center',
    'middle-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
  ];
  public notificationsByPosition: { [key in NotificationPosition]?: Observable<GlobalNotification[]> } = {};

  constructor(private notificationService: GlobalNotificationService) {}

  ngOnInit(): void {
    const allNotifications$ = this.notificationService.notifications$;

    this.positions.forEach((position) => {
      this.notificationsByPosition[position] = allNotifications$.pipe(
        map((notifications) => notifications.filter((n) => n.position === position)),
      );
    });
  }

  onClose(id: string): void {
    this.notificationService.remove(id);
  }

  trackById(index: number, notification: GlobalNotification): string {
    return notification.id;
  }
}
